/**
 * CSRF Token Utility
 * Handles CSRF token retrieval, caching, and refresh for API requests
 */

import { API_BASE_URL } from './apiConfig';

let csrfToken = null;
let tokenPromise = null;
let tokenExpiry = null;
const TOKEN_VALIDITY_MS = 23 * 60 * 60 * 1000; // 23 hours (tokens valid for 24h, refresh early)

/**
 * Get CSRF token from cookie (if available)
 */
function getCsrfTokenFromCookie() {
  if (typeof document === 'undefined') return null;
  
  const cookies = document.cookie.split(';');
  const csrfCookie = cookies.find(c => c.trim().startsWith('csrf_token='));
  if (csrfCookie) {
    return csrfCookie.split('=')[1].trim();
  }
  return null;
}

/**
 * Fetch CSRF token from API endpoint
 */
async function fetchCsrfToken() {
  try {
    const response = await fetch(`${API_BASE_URL}/api/csrf-token`, {
      method: 'GET',
      credentials: 'include', // Important: include cookies
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch CSRF token: ${response.status}`);
    }

    const data = await response.json();
    // Handle both 'csrf_token' and 'csrfToken' response formats
    return data.csrf_token || data.csrfToken || null;
  } catch (error) {
    console.error('Error fetching CSRF token:', error);
    throw error;
  }
}

/**
 * Get CSRF token (cached or fresh)
 * @returns {Promise<string>} CSRF token
 */
export async function getCsrfToken() {
  // Check if we have a valid cached token
  if (csrfToken && tokenExpiry && Date.now() < tokenExpiry) {
    return csrfToken;
  }

  // Check cookie first (might be set by a previous request)
  const cookieToken = getCsrfTokenFromCookie();
  if (cookieToken) {
    csrfToken = cookieToken;
    tokenExpiry = Date.now() + TOKEN_VALIDITY_MS;
    return csrfToken;
  }

  // If a token fetch is already in progress, wait for it
  if (tokenPromise) {
    return tokenPromise;
  }

  // Fetch new token
  tokenPromise = fetchCsrfToken()
    .then((token) => {
      csrfToken = token;
      tokenExpiry = Date.now() + TOKEN_VALIDITY_MS;
      tokenPromise = null;
      return token;
    })
    .catch((error) => {
      tokenPromise = null;
      throw error;
    });

  return tokenPromise;
}

/**
 * Clear cached CSRF token (force refresh on next request)
 */
export function clearCsrfToken() {
  csrfToken = null;
  tokenExpiry = null;
  tokenPromise = null;
}

/**
 * Check if CSRF token is needed for a request method
 * @param {string} method - HTTP method
 * @returns {boolean}
 */
export function needsCsrfToken(method) {
  const upperMethod = method?.toUpperCase();
  return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(upperMethod);
}

